import {
  dataMergeLogo,
  dbDirectLogo,
  dialpadLogo,
  dnbWordmarkLogo,
  docusignLogo,
  gmailWordmarkLogo,
  googlePlacesApiListLogo,
  googlePlacesApiLogo,
  hubspotLogo,
  moneythumbLogo,
  openCorpLogo,
  openCorporatesLogo,
  outlookLogo,
  salesforceWhiteLogo,
  serpApiListLogo,
  serpApiLogo,
  sharepointLogo,
  slackLogo,
  smartsheetLogo,
  twilioLogo,
  wordpressLogo,
  zohoLogo,
} from '@/assets/integrations';
import type { AvailableIntegration } from '@/types/integrations';

/**
 * Category + description + marquee logo metadata for the integrations
 * returned by `GET /agentous-integration-service/available-apps/public`.
 * The API only returns `{ key, name, logo }`, so this map fills in
 * the marketing copy, grouping, and (locally bundled) wordmark logos
 * used by the partner marquees.
 *
 * Keyed by the API's `key` field. The Integrations page itself keeps
 * using the colored PNG `logo` from the API for the grid cards — the
 * `marqueeLogo` below is only consumed by `IntegrationsLogoMarquee`,
 * which tints it white/black via a CSS filter for either background.
 */
export interface IntegrationMeta {
  category: IntegrationCategory;
  desc: string;
  /** Adds the card logo frame used for local fallback integrations. */
  framedLogo?: boolean;
  /** Local SVG used by the marquee. Leave undefined to skip in the
   *  marquee (e.g. icon-only marks, or integrations without a clean
   *  monochrome asset). */
  marqueeLogo?: string;
}

export type IntegrationCategory =
  | 'AI & Voice'
  | 'CRM'
  | 'Communication'
  | 'Documents'
  | 'Collaboration'
  | 'Developer Tools'
  | 'Finance'
  | 'Productivity';

export const INTEGRATION_CATEGORIES: readonly IntegrationCategory[] = [
  'AI & Voice',
  'CRM',
  'Communication',
  'Documents',
  'Collaboration',
  'Developer Tools',
  'Finance',
  'Productivity',
] as const;

export const INTEGRATION_META: Record<string, IntegrationMeta> = {
  datamerch: {
    category: 'Finance',
    desc: 'Connect DataMerch to access merchant performance and risk signals.',
    marqueeLogo: dataMergeLogo,
  },
  datamerge: {
    category: 'Finance',
    desc: 'Connect Data Merge to access merchant performance and risk signals.',
    marqueeLogo: dataMergeLogo,
  },
  'data-merge': {
    category: 'Finance',
    desc: 'Connect Data Merge to access merchant performance and risk signals.',
    marqueeLogo: dataMergeLogo,
  },
  dialpad: {
    category: 'Communication',
    desc: 'Connect Dialpad to manage calls, messages, meetings.',
    marqueeLogo: dialpadLogo,
  },
  docusign: {
    category: 'Documents',
    desc: 'Speed agreements and approvals with secure e-signatures.',
    marqueeLogo: docusignLogo,
  },
  dnb: {
    category: 'Finance',
    desc: 'Connect Dun & Bradstreet to enrich business identity and risk data.',
    framedLogo: true,
    marqueeLogo: dnbWordmarkLogo,
  },
  'dun-bradstreet': {
    category: 'Finance',
    desc: 'Connect Dun & Bradstreet to enrich business identity and risk data.',
    framedLogo: true,
    marqueeLogo: dnbWordmarkLogo,
  },
  'dun-and-bradstreet': {
    category: 'Finance',
    desc: 'Connect Dun & Bradstreet to enrich business identity and risk data.',
    framedLogo: true,
    marqueeLogo: dnbWordmarkLogo,
  },
  experian: {
    category: 'Finance',
    desc: 'Connect Experian to check identity and help reduce fraud risk.',
  },
  gmail: {
    category: 'Communication',
    desc: 'Connect Gmail to manage communication and follow-through.',
    marqueeLogo: gmailWordmarkLogo,
  },
  'google-places-api': {
    category: 'Developer Tools',
    desc: 'Connect Google Places API to enrich address, location, and business place data.',
    framedLogo: true,
    marqueeLogo: googlePlacesApiLogo,
  },
  hubspot: {
    category: 'CRM',
    desc: 'Connect HubSpot to manage leads, deals, and customer relationships.',
    marqueeLogo: hubspotLogo,
  },
  teams: {
    category: 'Communication',
    desc: 'Connect Teams to centralize meetings and teamwork.',
  },
  moneythumb: {
    category: 'Finance',
    desc: 'Turn statements into clean, reviewable financial data.',
    marqueeLogo: moneythumbLogo,
  },
  opencorporates: {
    category: 'Finance',
    desc: 'Connect OpenCorporates to verify company records and business identity.',
    framedLogo: true,
    marqueeLogo: openCorporatesLogo,
  },
  'outlook-personal': {
    category: 'Communication',
    desc: 'Connect Outlook to organize communication and schedules.',
    marqueeLogo: outlookLogo,
  },
  salesforce: {
    category: 'CRM',
    desc: 'Connect Salesforce to turn leads into revenue.',
    marqueeLogo: salesforceWhiteLogo,
  },
  serpapi: {
    category: 'Developer Tools',
    desc: 'Connect SerpAPI to retrieve search intelligence and web signals.',
    framedLogo: true,
    marqueeLogo: serpApiLogo,
  },
  'sharepoint-personal': {
    category: 'Documents',
    desc: 'Connect SharePoint to store files and collaborate.',
    marqueeLogo: sharepointLogo,
  },
  slack: {
    category: 'Communication',
    desc: 'Connect Slack to streamline communication, updates, collaboration.',
    marqueeLogo: slackLogo,
  },
  smartsheet: {
    category: 'Productivity',
    desc: 'Connect Smartsheet to track work and progress.',
    marqueeLogo: smartsheetLogo,
  },
  twilio: {
    category: 'Communication',
    desc: 'Connect Twilio to automate calls, texts, alerts.',
    marqueeLogo: twilioLogo,
  },
  wordpress: {
    category: 'Documents',
    desc: 'Connect WordPress to publish and update content.',
    marqueeLogo: wordpressLogo,
  },
  'zoho-crm': {
    category: 'CRM',
    desc: 'Connect Zoho to manage customers, tasks, and growth.',
    marqueeLogo: zohoLogo,
  },
};

export const LOCAL_INTEGRATIONS: readonly AvailableIntegration[] = [
  {
    key: 'google-places-api',
    name: 'Google Places API',
    logo: googlePlacesApiListLogo,
  },
  {
    key: 'opencorporates',
    name: 'OpenCorporates',
    logo: openCorpLogo,
  },
  {
    key: 'serpapi',
    name: 'SerpAPI',
    logo: serpApiListLogo,
  },
  {
    key: 'dnb',
    name: 'Dun & Bradstreet',
    logo: dbDirectLogo,
  },
] as const;

export const mergeLocalIntegrations = (
  integrations: readonly AvailableIntegration[]
): AvailableIntegration[] => {
  const seenKeys = new Set(
    integrations.map(integration => integration.key.trim().toLowerCase())
  );
  const seenNames = new Set(
    integrations.map(integration => integration.name.trim().toLowerCase())
  );

  return [
    ...integrations,
    ...LOCAL_INTEGRATIONS.filter(integration => {
      const key = integration.key.trim().toLowerCase();
      const name = integration.name.trim().toLowerCase();
      return !seenKeys.has(key) && !seenNames.has(name);
    }),
  ];
};

export const getIntegrationMeta = (
  key: string
): IntegrationMeta | undefined => {
  return INTEGRATION_META[key.trim().toLowerCase()];
};
